/**
 * The two readouts a player in a headset cannot do without: what is under the
 * crosshair, and whether they are about to die.
 *
 * Both mirror `ui/hud.ts` rather than reusing it — that file owns DOM nodes and
 * keeps its state private, and it is not this batch's to change. The rules are
 * restated here and the two must stay in step: a vital appears below 40, the
 * lamp exists only while the lamp does, and the noise row fades in the first
 * time somebody sprints. Nothing is permanently pinned. A health bar bolted to
 * the corner of your eye for a whole run would wreck the one thing this game
 * is trying to do.
 */

import { TUNING } from '../../core/config';
import { clamp01 } from '../../core/math';
import type { EntityMode, EntityState, GameContext } from '../../core/types';
import { CanvasPanel } from './panel';
import type { PointerStyle } from './pointer';
import { AMBER, AMBER_DIM, AMBER_FAINT, BAD, bar, face, fitPx, LINE, text } from './theme';

/** Below this a vital fades in. Mirrors `VITAL_SHOW` in `ui/hud.ts`. */
const VITAL_SHOW = 40;

/** Mirrors `HUNTING` in `ui/hud.ts`: something is actually coming for you. */
const HUNTING: ReadonlySet<EntityMode> = new Set<EntityMode>(['investigate', 'stalk', 'chase', 'attack']);

/**
 * What the prompt tells the player to press, by what the session turned out to
 * have.
 *
 * The DOM prompt draws a `<kbd>E</kbd>`. In a headset there is no E, and this
 * is one of only two places in the in-world UI that names a control (the other
 * is the menu's bottom line). It cannot be a constant: an Apple Vision Pro has
 * no trigger and telling somebody to pull one is worse than saying nothing.
 * `unknown` keeps the controller wording, because a headset with controllers
 * reports them on the first frame and a headset without them does too.
 */
const INTERACT_LABEL: Record<PointerStyle, string> = {
  unknown: 'TRIGGER',
  stick: 'TRIGGER',
  point: 'PINCH',
};

/** Seconds a panel takes to fade in or out. Slow enough to read as arriving. */
export const FADE = 0.22;

/* ------------------------------------------------------------------ *
 * The interaction prompt
 * ------------------------------------------------------------------ */

/**
 * "Take Almond Water", in the room.
 *
 * The highest-priority panel in the batch by a distance: without it nothing in
 * the building can be picked up or used, because the player has no way of
 * knowing there is anything there. It is also the only panel that is genuinely
 * transient — it exists for as long as something is under the crosshair and not
 * a moment longer, which is what stops it from becoming furniture.
 */
export class PromptPanel {
  readonly panel = new CanvasPanel(0.66, 0.115);
  private shown: string | null = null;
  private alpha = 0;
  private style: PointerStyle = 'unknown';

  constructor() {
    this.panel.setOpacity(0);
  }

  /** The latest `ui:prompt`. Repaints only when the words changed. */
  set(textValue: string | null): void {
    if (textValue === this.shown) return;
    this.shown = textValue;
    if (textValue) this.draw(textValue);
  }

  /** What this session's hardware calls the primary action. */
  setInputStyle(style: PointerStyle): void {
    if (style === this.style) return;
    this.style = style;
    if (this.shown) this.draw(this.shown);
  }

  update(dt: number, visible: boolean): void {
    const target = visible && this.shown ? 1 : 0;
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / FADE);
    this.panel.setOpacity(this.alpha);
  }

  private draw(label: string): void {
    const p = this.panel;
    p.paint((c, w, h) => {
      face(c, w, h, LINE);
      const pad = p.px(0.022);
      const mid = h / 2;
      const key = p.px(0.019);
      // The key tag, drawn as the DOM draws its <kbd>: a boxed, tracked word in
      // the dim amber, so the eye reads the instruction and not the box.
      const keyW = p.px(0.135);
      const keyH = p.px(0.05);
      c.strokeStyle = LINE;
      c.lineWidth = Math.max(1, p.px(0.0018));
      c.strokeRect(pad, mid - keyH / 2, keyW, keyH);
      const word = INTERACT_LABEL[this.style];
      const keyStyle = { px: key, color: AMBER_DIM, align: 'center' as const, tracking: p.px(0.0016) };
      text(c, word, pad + keyW / 2, mid, { ...keyStyle, px: fitPx(c, word, keyW - p.px(0.012), keyStyle) });
      const textX = pad + keyW + p.px(0.026);
      const style = { px: p.px(0.031), color: AMBER };
      text(c, label, textX, mid, { ...style, px: fitPx(c, label, w - textX - pad, style) });
    });
  }

  dispose(): void {
    this.panel.dispose();
  }
}

/* ------------------------------------------------------------------ *
 * Vitals
 * ------------------------------------------------------------------ */

interface Row {
  key: string;
  value: number;
  shown: boolean;
  crit: boolean;
}

export class VitalsPanel {
  readonly panel = new CanvasPanel(0.42, 0.27);

  private readonly rows: Row[] = [
    { key: 'LAMP', value: 0, shown: false, crit: false },
    { key: 'NOISE', value: 0, shown: false, crit: false },
    { key: 'HEALTH', value: 0, shown: false, crit: false },
    { key: 'SANITY', value: 0, shown: false, crit: false },
  ];
  /** Seconds the noise row stays up after the player goes quiet. */
  private noiseHold = 0;
  private alpha = 0;
  private dirty = true;

  constructor() {
    this.panel.setOpacity(0);
  }

  /**
   * Sample the run. `dt` is the *simulation* delta so the noise hold does not
   * tick down behind a pause menu, exactly as in `ui/hud.ts`.
   */
  sample(ctx: GameContext): void {
    const U = TUNING.ui;
    const lampOn = ctx.player.flashlightOn;
    const noise = clamp01(ctx.player.noise);
    const hunted = isHunted(ctx.entities);

    if (noise >= U.noiseShow || hunted) this.noiseHold = U.noiseHold;
    else if (ctx.dt > 0) this.noiseHold = Math.max(0, this.noiseHold - ctx.dt);

    this.set(0, ctx.player.flashlightCharge, lampOn, ctx.player.flashlightCharge < TUNING.flashlight.dyingThreshold);
    this.set(1, noise * 100, this.noiseHold > 0, hunted && noise >= U.noiseLoud);
    this.set(2, ctx.stats.health, ctx.stats.health < VITAL_SHOW, ctx.stats.health < TUNING.survival.criticalThreshold);
    this.set(3, ctx.stats.sanity, ctx.stats.sanity < VITAL_SHOW, ctx.stats.sanity < TUNING.survival.criticalThreshold);
  }

  update(dt: number, visible: boolean): void {
    if (this.dirty) {
      this.dirty = false;
      this.draw();
    }
    const any = this.rows.some((r) => r.shown);
    const target = visible && any ? 1 : 0;
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / FADE);
    this.panel.setOpacity(this.alpha);
  }

  dispose(): void {
    this.panel.dispose();
  }

  /** Quantised to whole points, so a slow drain repaints a few dozen times. */
  private set(i: number, value: number, shown: boolean, crit: boolean): void {
    const row = this.rows[i]!;
    const q = Math.max(0, Math.round(value));
    if (row.value === q && row.shown === shown && row.crit === crit) return;
    row.value = q;
    row.shown = shown;
    row.crit = crit;
    this.dirty = true;
  }

  /**
   * Rows are packed from the bottom, so a row appearing pushes the stack up
   * rather than shifting the ones already being read.
   */
  private draw(): void {
    const p = this.panel;
    p.paint((c, w, h) => {
      face(c, w, h, LINE);
      const pad = p.px(0.024);
      const rowH = p.px(0.052);
      const label = p.px(0.019);
      const num = p.px(0.024);
      let y = h - pad - rowH / 2;
      for (let i = this.rows.length - 1; i >= 0; i--) {
        const row = this.rows[i]!;
        if (!row.shown) continue;
        const colour = row.crit ? BAD : AMBER;
        text(c, row.key, pad, y - p.px(0.014), {
          px: label,
          color: row.crit ? BAD : AMBER_FAINT,
          tracking: p.px(0.0014),
        });
        text(c, row.value < 10 ? `0${row.value}` : String(row.value), w - pad, y - p.px(0.014), {
          px: num,
          color: colour,
          align: 'right',
        });
        bar(c, pad, y + p.px(0.008), w - pad * 2, p.px(0.009), row.value / 100, colour);
        y -= rowH;
      }
    });
  }
}

/** Mirrors `isHunted` in `ui/hud.ts`. */
function isHunted(entities: readonly EntityState[]): boolean {
  for (const e of entities) {
    if (e.distToPlayer <= TUNING.ui.noiseHuntRange && HUNTING.has(e.mode)) return true;
  }
  return false;
}
